import trackMixpanelEvent from '@/common/utils/mixpanel';
import { getNewDailyActivities } from '@/common/utils/student';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getDailies,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import {
  Reward,
  RewardsManager,
  RewardType,
} from '@/game/GameComponents/PhaserGame/Utils/RewardsManager';

export class DailiesManager {
  static _instance?: DailiesManager;

  constructor() {
    if (DailiesManager.instance !== undefined) {
      return;
    }
    DailiesManager._instance = this;

    // Event listeners. These need to be registered directly to the event bus because they are not part of a scene.
    EventBus.on(GAME_EVENTS.PLAYER_WON_DUEL, this.handlePlayerWonDuel, this);
    EventBus.on(
      GAME_EVENTS.PLAYER_CLAIMED_REWARD,
      this.handlePlayerClaimedReward,
      this,
    );
    EventBus.on(
      GAME_EVENTS.DAILY_PANEL_OPENED,
      this.handleDailyPanelOpened,
      this,
    );
    EventBus.on(GAME_EVENTS.TILEMAP_LEVEL_INITIALIZED, this.dailyCheck, this);
  }

  static get instance(): DailiesManager {
    return DailiesManager._instance!;
  }

  static destroyDailiesManager() {
    DailiesManager._instance = undefined;
  }

  // State updates.
  completeActivity(activity: string) {
    let newDailyCompleted = false;

    switch (activity) {
      case 'winDuel':
        const duelState = getDailies().duel;
        if (duelState.shouldCompleteDuel && !duelState.hasCompletedDuel) {
          // We just completed our duel daily.
          newDailyCompleted = true;
          trackMixpanelEvent('User Completed Duel Daily');
        }
        GameInstance.instance.updateDailyActivities({
          duel: {
            ...GameInstance.instance.student.dailyActivities.duel,
            hasCompletedDuel: true,
          },
        });
        break;
      case 'mysticMatch':
      case 'wordWeaver':
      case 'phonemeForge':
      case 'trickyWords':
      case 'sentenceBuilder':
      case 'wordRifts':
      case 'wordSums':
        const minigamesState = getDailies().minigames;
        if (
          minigamesState.numCompletedMinigames <
          minigamesState.targetNumMinigames
        ) {
          // We just completed a minigame daily.
          newDailyCompleted = true;
          trackMixpanelEvent('User Completed Daily Minigame', {
            activityName: activity,
          });
        }

        // Mixpanel mastery tracking.
        const accuracy = getSession().currentActivity?.accuracy || 0;
        if (accuracy >= 90) {
          trackMixpanelEvent('User Mastered Minigame', {
            game: activity,
            accuracy,
          });
        }
        // For now. This will all need to be refactored as part of the phaser minigame integration.
        GameInstance.instance.updateDailyActivities({
          minigames: {
            ...GameInstance.instance.student.dailyActivities.minigames,
            numCompletedMinigames: Math.min(
              GameInstance.instance.student.dailyActivities.minigames
                .targetNumMinigames,
              GameInstance.instance.student.dailyActivities.minigames
                .numCompletedMinigames + 1,
            ),
          },
        });
    }

    // Fire the mixpanel event if the user has completed all daily activities.
    if (
      newDailyCompleted &&
      RewardsManager.getTotalDailyProgress(getDailies()) === 1
    ) {
      trackMixpanelEvent('User Completed All Daily Activities');
    }
  }

  updateDailiesAfterClaimingReward(reward: Reward) {
    switch (reward.rewardType) {
      case RewardType.DAILY_MINIGAME:
        GameInstance.instance.updateDailyActivities({
          minigames: {
            ...getDailies().minigames,
            numRewardsClaimed: getDailies().minigames.numRewardsClaimed + 1,
          },
        });
        break;
      case RewardType.DAILY_DUEL:
        GameInstance.instance.updateDailyActivities({
          duel: {
            ...getDailies().duel,
            hasClaimedDuelReward: true,
          },
        });
        break;
    }
  }

  setHasViewedDailies(hasViewedDailyActivities: boolean) {
    GameInstance.instance.updateStudent({
      dailyActivities: {
        ...GameInstance.instance.student.dailyActivities,
        hasViewedDailyActivities,
      },
    });
  }

  /*
   * Checks and potentially updates the student's daily activities.
   */
  dailyCheck = () => {
    const student = getStudent();
    const dailyActivities = student.dailyActivities;
    const lastRefresh = dailyActivities.lastRefresh;
    const settings = student.settings;
    const lastRefreshDate = new Date(lastRefresh);
    const thisDate = new Date();

    // To handle the case where lastRefresh hasn't been set yet
    const validLastRefresh = !isNaN(lastRefreshDate.getTime());

    const sameDay =
      validLastRefresh &&
      lastRefreshDate.getDate() === thisDate.getDate() &&
      lastRefreshDate.getMonth() === thisDate.getMonth() &&
      lastRefreshDate.getFullYear() === thisDate.getFullYear();

    if (student && !sameDay) {
      // update daily activities
      GameInstance.instance.updateStudent({
        dailyActivities: getNewDailyActivities(settings),
      });
    }
  };

  // Event handlers.
  handlePlayerWonDuel() {
    this.completeActivity('winDuel');
  }

  handlePlayerClaimedReward(reward: Reward) {
    this.updateDailiesAfterClaimingReward(reward);
  }

  handleDailyPanelOpened() {
    this.setHasViewedDailies(true);
  }
}

export const dailiesManager = new DailiesManager();
